// Display logic for the blue session-limit warning banner.
//
// The banner reappears every time usage crosses into a new 10% bucket within the
// same period. e.g. dismiss at 62% (bucket 60) -> stays hidden until usage hits
// 70% (bucket 70), where it shows again, and so on. A new period (different
// periodEnd) resets the state from scratch.

// Below this percentage we never show the blue banner.
export const SESSION_LIMIT_BANNER_THRESHOLD = 60;
// At/above this percentage the amber "limit reached" banner takes over.
export const SESSION_LIMIT_REACHED_THRESHOLD = 100;
const THRESHOLD_BUCKET_SIZE = 10;

// Dismissal state persisted per period. `threshold` is the 10% bucket the user
// was in when they dismissed (60, 70, 80, 90).
export interface SessionLimitDismissal {
  periodEnd: string;
  threshold: number;
}

// Floors a percentage to its 10% bucket (62 -> 60, 71 -> 70).
const getThresholdBucket = (percentage: number) =>
  Math.floor(percentage / THRESHOLD_BUCKET_SIZE) * THRESHOLD_BUCKET_SIZE;

export const parseDismissal = (raw: string | null): SessionLimitDismissal | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.periodEnd === 'string' &&
      typeof parsed.threshold === 'number'
    ) {
      return { periodEnd: parsed.periodEnd, threshold: parsed.threshold };
    }
  } catch {
    // Legacy/non-JSON value (older period-string format) — treat as no dismissal.
  }
  return null;
};

// Builds the dismissal record to persist for the current usage snapshot.
export const buildDismissal = (periodEnd: unknown, percentage: number): SessionLimitDismissal => ({
  periodEnd: String(periodEnd ?? ''),
  threshold: getThresholdBucket(percentage),
});

// Whether the blue warning banner should show for the current usage snapshot.
export const shouldShowHighUsageBanner = (
  percentage: number,
  periodEnd: unknown,
  dismissal: SessionLimitDismissal | null,
): boolean => {
  if (percentage < SESSION_LIMIT_BANNER_THRESHOLD || percentage >= SESSION_LIMIT_REACHED_THRESHOLD) return false;
  // New period (or never dismissed) — show from scratch.
  if (!dismissal || dismissal.periodEnd !== String(periodEnd ?? '')) return true;
  // Same period — show only once usage climbs into a higher 10% bucket.
  return getThresholdBucket(percentage) > dismissal.threshold;
};
